import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import IdentityFields from './components/form/IdentityFields';
import ContactFields from './components/form/ContactFields';
import IdProofFields from './components/form/IdProofFields';
import NotesField from './components/form/NotesField';
import { normalizePhone } from './visitorConstants';
import { validateVisitor, buildVisitorPayload } from './visitorFormValidation';

const EMPTY = {
    name: '',
    phone: '',
    email: '',
    company_name: '',
    visitor_type: 'GUEST',
    id_proof_type: '',
    id_proof_number: '',
    photo_url: '',
    address: '',
    notes: '',
};

// Shared create/edit form. The parent owns submission; this component holds the
// field state and hands back a clean payload.
//
// society_id deliberately lives outside this form: it is added by the parent on
// create and must never be sent on update, since a visitor cannot move between
// societies.
const VisitorForm = ({
    initial,
    onSubmit,
    onCancel,
    submitting = false,
    mode = 'create',
    submitLabel,
    requireIdProof = false,
}) => {
    const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
    const [errors, setErrors] = useState({});
    // The normalisation hint only appears after blur — rewriting the number
    // mid-typing fights the user.
    const [phoneTouched, setPhoneTouched] = useState(false);

    useEffect(() => {
        if (initial) setForm(f => ({ ...f, ...initial }));
    }, [initial]);

    const set = (k, v) => {
        setForm(f => ({ ...f, [k]: v }));
        setErrors(e => ({ ...e, [k]: undefined }));
    };

    const normalizedPhone = useMemo(() => normalizePhone(form.phone), [form.phone]);
    // Only worth mentioning when what gets stored differs from what was typed.
    const phoneWillChange = phoneTouched
        && !!form.phone.trim()
        && normalizedPhone !== form.phone.trim();

    const handleSubmit = (ev) => {
        ev.preventDefault();
        const found = validateVisitor(form, { requireIdProof });
        setErrors(found);
        if (Object.keys(found).length > 0) return;
        onSubmit(buildVisitorPayload(form));
    };

    const fieldProps = { form, errors, set };

    return (
        <form onSubmit={handleSubmit} className="tw-space-y-5">
            <IdentityFields {...fieldProps} />

            <ContactFields
                {...fieldProps}
                mode={mode}
                normalizedPhone={normalizedPhone}
                phoneWillChange={phoneWillChange}
                onPhoneBlur={() => setPhoneTouched(true)}
            />

            <IdProofFields {...fieldProps} required={requireIdProof} />

            <NotesField form={form} set={set} />

            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2 tw-pt-3 tw-border-t tw-border-gray-100">
                <button
                    type="button"
                    onClick={onCancel}
                    className="tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={submitting}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700 tw-rounded-md disabled:tw-opacity-50"
                >
                    <Icon icon={mode === 'create' ? 'mdi:account-plus-outline' : 'mdi:content-save-outline'} className="tw-text-base" />
                    {submitting
                        ? 'Saving...'
                        : (submitLabel || (mode === 'create' ? 'Register Visitor' : 'Save Changes'))}
                </button>
            </div>
        </form>
    );
};

export default VisitorForm;
